/**
 * The click-to-call primitive: renders a phone number with a Phone icon. For
 * operators holding use:crm.calls it opens the in-app call modal; everyone
 * else gets a plain tel: anchor, so the number stays actionable either way.
 * Pass stopPropagation inside clickable table rows.
 */
import { Phone } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import { formatPhone, normalizeE164 } from "../../lib/phone";
import type { CallSource } from "../../lib/calls";
import { useCallContext } from "./CallProvider";
import { useSuppressedNumbers } from "./useSuppressedNumbers";

export interface PhoneLinkProps {
  phone: string;
  source: CallSource;
  companyName?: string;
  regCode?: string;
  leadDomain?: string;
  /** The CRM record this number was printed on, so the call files itself. */
  clientId?: string;
  leadId?: string;
  /** The person this number belongs to, when it is a contact's own number and
   *  not the company switchboard. The server derives the client from it. */
  contactId?: string;
  className?: string;
  /** Swallow the click so a clickable parent row does not also fire. */
  stopPropagation?: boolean;
  /** Hide the leading Phone icon (dense table cells). */
  hideIcon?: boolean;
}

export function PhoneLink({
  phone,
  source,
  companyName,
  regCode,
  leadDomain,
  clientId,
  leadId,
  contactId,
  className,
  stopPropagation,
  hideIcon,
}: PhoneLinkProps) {
  const ctx = useCallContext();
  const t = useT();
  const suppressed = useSuppressedNumbers();
  const normalized = normalizeE164(phone);
  const label = normalized ? formatPhone(normalized) : phone;
  const canCall = !!ctx?.enabled;
  // The one place this has to be drawn. Every screen that prints a number goes
  // through here, so striking it through once covers all of them - and an
  // operator who can only see it after dialling and being refused has already
  // spent the click.
  const blocked = !!suppressed?.has(normalized);

  const baseClass = cn(
    "inline-flex items-center gap-1.5 text-sm hover:underline",
    blocked ? "text-muted-foreground line-through decoration-2" : "text-primary",
    className,
  );
  const title = blocked ? t("calls.suppression.marked") : undefined;

  if (!canCall) {
    return (
      <a
        href={`tel:${normalized ?? phone}`}
        className={baseClass}
        title={title}
        onClick={(e) => {
          if (stopPropagation) e.stopPropagation();
        }}
      >
        {!hideIcon && <Phone className="size-3" />}
        {label}
      </a>
    );
  }

  return (
    <button
      type="button"
      className={baseClass}
      title={title}
      onClick={(e) => {
        if (stopPropagation) e.stopPropagation();
        ctx!.startCall({ phone, companyName, regCode, leadDomain, clientId, leadId, contactId, source });
      }}
    >
      {!hideIcon && <Phone className="size-3" />}
      {label}
    </button>
  );
}
